<script lang="ts" setup>
const { t: $t } = useI18n()

import type { ApiCustomerModuleDefectGetAll, CustomerModuleDefectCreateOrUpdatePayload, CustomerModuleDefectEntity } from '~/layers/customer-module/types'

const props = defineProps<{
  entity: CustomerModuleDefectEntity
  entityId: string
  disableDefectCreation?: boolean
  accessCodeSession?: string
  isForeignMachineryDefectsPage?: boolean
}>()
defineEmits<{ (e: 'close'): void }>()

const fulltextSearchFilters: FulltextSearchFieldFilter[] = ['/machineryId', '/machinery/producerCompanyName', '/machineryAccessoryId', '/machineryAccessory/category', '/machineryAccessory/producerCompanyName']
const { fulltextSearchValue, where: fullTextSearchWhere } = useFilterData({ filters: fulltextSearchFilters })

const entity = computed(() => props.entity)
const entityId = computed(() => props.entityId)
const accessCodeSession = computed(() => props.accessCodeSession)

const showRepairedDefects = ref(!props.isForeignMachineryDefectsPage)

const where = computed(() => ({
  ...fullTextSearchWhere.value,
  repairedAt: showRepairedDefects.value ? undefined : null,
}))

const { useremail } = useAuthorization()

const { defectPublicQuery } = useCustomerModuleQuery()
const { data: defects, isLoading, isRefetching, error } = defectPublicQuery.getAllByEntityId(entity, entityId, where, accessCodeSession)

// Table Column
const columns = computed((): OneDataColumn<ApiCustomerModuleDefectGetAll>[] => [
  {
    key: 'type',
    title: $t('customerModule.defect.type.label'),
    render: defect => $t(`customerModule.defect.type.${defect.type}`),
  },
  {
    key: 'machineryId',
    title: $t('customerModule.defect.id.label'),
    render: defect => defect.machineryId ?? defect.machineryAccessoryId,
  },
  {
    key: 'repairedAt',
    title: $t('customerModule.defect.status.label'),
    render: defect => defect.repairedAt ? $t('customerModule.defect.status.repaired') : $t('customerModule.defect.status.open'),
  },
  {
    key: 'createdBy',
    title: $t('customerModule.defect.createdBy.label'),
    render: (defect) => {
      if (defect.createdBy) {
        return defect.createdBy.name ?? defect.createdBy.email
      }
      if (defect.contactPersonName) {
        let display = defect.contactPersonName

        if (defect.contactPersonEmail) {
          display += ` [${$t('customerModule.defect.createdBy.user.email')}: ${defect.contactPersonEmail}]`
        }

        if (defect.contactPersonPhone) {
          display += ` [${$t('customerModule.defect.createdBy.user.phone')}: ${defect.contactPersonPhone}]`
        }

        return display
      }
    },
    hideIfNullish: true,
  },
  {
    key: 'createdByAccessCodeSession',
    title: $t('customerModule.defect.createdBy.label'),
    render: defect => defect.createdByAccessCodeSession?.name ?? defect.createdByAccessCodeSession?.email,
    hideIfNullish: true,
  },
  {
    key: 'createdAt',
    title: $t('customerModule.defect.createdAt.label'),
    render: defect => useDateAsString(defect.createdAt),
  },
  {
    key: 'description',
    title: $t('customerModule.defect.description.label'),
    hideIfNullish: true,
  },
])

function actionButtons(defect: ApiCustomerModuleDefectGetAll): CustomerModuleAction[] {
  if (defect.repairedAt) {
    return ['view', 'showComments']
  }

  const actions: CustomerModuleAction[] = defect.createdBy?.email === useremail.value
    ? ['edit', 'markAsRepaired', 'showComments']
    : ['view', 'markAsRepaired', 'showComments']

  if (defect.isExpressRepairRequested) {
    actions.push('withdrawExpressRepairRequest')
  } else {
    actions.push('requestExpressRepair')
  }

  return actions
}

function openDefectCreateOrUpdateModal({ id, offerId, machineryId }: ApiCustomerModuleDefectGetAll) {
  const payloadEntityId = entity.value === 'offer' ? offerId : machineryId
  if (!payloadEntityId) {
    return
  }

  defectCreateOrUpdatePayload.value = { mode: 'update', id, entity: entity.value, entityId: payloadEntityId }
}

function getListContainerClass(defect: ApiCustomerModuleDefectGetAll) {
  if (defect.isExpressRepairRequested && !defect.repairedAt) {
    return 'border-orange-500 border-2'
  }
  return ''
}
function getListActionButtonClass(action: CustomerModuleAction, defect: ApiCustomerModuleDefectGetAll) {
  if (action === 'withdrawExpressRepairRequest' && !defect.repairedAt) {
    return '!text-orange-500'
  }
  return ''
}

const defectCreateOrUpdatePayload = ref<CustomerModuleDefectCreateOrUpdatePayload | undefined>(undefined)
const defectDetailsPayload = ref<ApiCustomerModuleDefectGetAll | undefined>(undefined)
const defectIdToMarkAsRepaired = ref<string | undefined>(undefined)
const defectIdToShowComments = ref<string | undefined>(undefined)

const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()

const updateExpressRepairRequest = useMutation({
  mutationFn: $trpc.customerModule.defect.updateExpressRepairRequest.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('customerModule.defect.expressRepair.notification.error') }),
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: [CUSTOMER_MODULE_QUERY_KEY, 'defects'] })
    notification.success({ title: $t('customerModule.defect.expressRepair.notification.success'), duration: 4500 })
  },
})
</script>

<template>
  <CustomerModuleDataCard :title="$t('customerModule.defect.card.title')">
    <template #headerExtra>
      <div class="flex flex-col sm:flex-row items-center gap-2">
        <n-checkbox v-if="props.isForeignMachineryDefectsPage" v-model:checked="showRepairedDefects" :label="$t('customerModule.defect.card.showRepaired')" />

        <n-button
          v-if="entityId"
          type="primary"
          :disabled="disableDefectCreation"
          secondary
          class="w-fullsm:w-auto"
          @click="defectCreateOrUpdatePayload = { mode: 'create', entity, entityId, isForeignMachineryDefect: props.isForeignMachineryDefectsPage }"
        >
          <template #icon>
            <Icon name="material-symbols:add-box-outline-rounded" />
          </template>
          {{ $t('customerModule.defect.create') }}
        </n-button>
      </div>
    </template>

    <n-input v-model:value="fulltextSearchValue" :loading="isRefetching" :placeholder="$t('customerModule.defect.search.placeholder')">
      <template #prefix>
        <Icon name="material-symbols:search" />
      </template>
    </n-input>

    <n-divider class="!my-4" />
    <CustomerModuleList
      class="mb-4"
      :data="defects ?? []"
      :is-loading="isLoading || isRefetching"
      :error
      :columns
      :action-buttons
      :get-container-class="getListContainerClass"
      :get-action-button-class="getListActionButtonClass"
      @request-express-repair="defect => updateExpressRepairRequest.mutate({ id: defect.id, isExpressRepairRequested: true })"
      @withdraw-express-repair-request="defect => updateExpressRepairRequest.mutate({ id: defect.id, isExpressRepairRequested: false })"
      @mark-as-repaired="defectIdToMarkAsRepaired = $event.id"
      @show-comments="defectIdToShowComments = $event.id"
      @edit="openDefectCreateOrUpdateModal"
      @view="defectDetailsPayload = $event"
    />

    <n-button v-if="!isForeignMachineryDefectsPage" secondary class="min-w-32" @click="$emit('close')">
      {{ $t('customerModule.button.close') }}
    </n-button>
  </CustomerModuleDataCard>

  <CustomerModuleDefectRepairContainer
    v-if="defectIdToMarkAsRepaired"
    :id="defectIdToMarkAsRepaired"
    :access-code-session="accessCodeSession"
    @close="defectIdToMarkAsRepaired = undefined"
  >
    {{ $t('customerModule.defect.repair.confirm') }}
  </CustomerModuleDefectRepairContainer>

  <CustomerModuleDefectCreateOrUpdateContainer
    :payload="defectCreateOrUpdatePayload"
    :access-code-session="accessCodeSession"
    @close="defectCreateOrUpdatePayload = undefined"
  />

  <CustomerModuleDefectDetailsPopup
    v-if="defectDetailsPayload"
    :payload="defectDetailsPayload"
    @close="defectDetailsPayload = undefined"
  />

  <CustomerModuleDefectCommentsPopup
    v-model:defect-id="defectIdToShowComments"
  />
</template>
